// frontend/src/pages/AcceptInvitationPage.jsx

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  Box, Typography, TextField, Button, Paper, 
  CircularProgress, Alert, Container 
} from '@mui/material';
import teamService from '../services/teamService';
import { useNotification } from '../context/NotificationContext';

const AcceptInvitationPage = () => {
  const { showSuccess } = useNotification();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState(null);
  const [invitationDetails, setInvitationDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    nome_completo: '',
    password: '',
    confirmPassword: '',
    cpf: '',
    telefone: '',
    cargo: '',
  });

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
      const fetchDetails = async () => {
        try {
          const response = await teamService.getInvitationDetails(urlToken);
          setInvitationDetails(response.data);
          setError('');
        } catch (err) {
          setError('Convite inválido, expirado ou já utilizado. Por favor, verifique o link ou peça um novo convite.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchDetails();
    } else {
      setError('Token de convite não fornecido.');
      setIsLoading(false);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // --- ENVIANDO CPF E TELEFONE PARA A API ---
      await teamService.acceptInvitation({
        token,
        nome_completo: formData.nome_completo,
        password: formData.password,
        cpf: formData.cpf,
        telefone: formData.telefone,
        cargo: formData.cargo,
      });

      showSuccess('Cadastro realizado com sucesso! Você será redirecionado para a página de login.');
      navigate('/login');

    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Falha ao finalizar o cadastro.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Container component="main" maxWidth="xs" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={6} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h4" gutterBottom>
          Bem-vindo(a)!
        </Typography>

        {error ? (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>
        ) : (
            <>
                <Typography component="p" variant="subtitle1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
                    Você foi convidado para se juntar ao aplicativo de assinaturas digitais da Equipe <strong>{invitationDetails?.company_name}</strong>.
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        value={invitationDetails?.email || ''}
                        disabled
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="nome_completo"
                        label="Seu Nome Completo"
                        name="nome_completo"
                        autoComplete="name"
                        autoFocus
                        value={formData.nome_completo}
                        onChange={handleChange}
                    />
                    {/* --- --- */}
                    <TextField
                        margin="normal"
                        fullWidth
                        name="cargo"
                        label="Cargo"
                        id="cargo"
                        value={formData.cargo}
                        onChange={handleChange}
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        name="cpf"
                        label="CPF"
                        id="cpf"
                        value={formData.cpf}
                        onChange={handleChange}
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        name="telefone"
                        label="Telefone"
                        id="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Crie uma Senha"
                        type="password"
                        id="password"
                        value={formData.password}
                        onChange={handleChange}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirme a Senha"
                        type="password"
                        id="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{ mt: 3, mb: 2, py: 1.5 }}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Finalizar Cadastro e Entrar'}
                    </Button>
                </Box>
            </>
        )}
        <Link to="/login" style={{ marginTop: '16px' }}>
            Já tem uma conta? Faça login
        </Link>
      </Paper>
    </Container>
  );
};

export default AcceptInvitationPage;